import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './components/AuthProvider';
import { getAdAttribution } from './lib/adAttribution';
import {
  generateUgcIdeas,
  getLibraryCharacters,
  getMarketingWebsiteScanStatus,
  initializeProject,
  sendCAPIEvent,
  startAgentCompose,
  updateMarketingCharacterSelection,
} from './lib/api';
import { trackMetaCustomEvent, trackMetaEvent } from './lib/meta';
import './PostSignupPage.css';

const FILTER_GROUPS = {
  Sex: ['male', 'female'],
  Age: ['young adult', 'middle aged', 'old'],
  Race: ['white', 'black', 'asian', 'hispanic', 'middle eastern', 'south asian'],
};

function hasKeyword(keywords, value) {
  if (!keywords || !Array.isArray(keywords)) return false;
  return keywords.some((keyword) => keyword.toLowerCase() === value.toLowerCase());
}

function buildUgcPrompt(script, addCaptions = false) {
  const captionLine = addCaptions ? '\n\nAdd captions to all videos with speech.' : '';
  return (
    'Create a UGC-style product ad video. Use the ugc-video skill. ' +
    'Use the selected character details from the brief and the attached product media. Script:\n\n' +
    script +
    captionLine
  );
}

function MarketingCharacterOnboardingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanResult, setScanResult] = useState(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);
  const [persistedCharacterId, setPersistedCharacterId] = useState(null);
  const [libraryCharacters, setLibraryCharacters] = useState([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({ Sex: [], Age: [], Race: [], Other: [] });
  const [otherKeywords, setOtherKeywords] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    const eventId = trackMetaEvent('ViewContent', {
      content_type: 'marketing_onboarding_character',
      content_name: 'Marketing Onboarding Character Step',
    });
    sendCAPIEvent('ViewContent', eventId, {
      content_type: 'marketing_onboarding_character',
      content_name: 'Marketing Onboarding Character Step',
    }, getAdAttribution()).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;

    (async () => {
      try {
        const [scan, libraryResp] = await Promise.all([
          getMarketingWebsiteScanStatus(),
          getLibraryCharacters(),
        ]);
        if (cancelled) return;

        const status = scan?.status || 'idle';
        setScanStatus(status);
        setScanResult(scan || null);
        setLibraryCharacters(libraryResp?.characters || []);
        setSelectedCharacterId(scan?.selectedCharacterId || null);
        setPersistedCharacterId(scan?.selectedCharacterId || null);

        if (status !== 'completed') {
          navigate('/onboarding/marketing', { replace: true });
        }
      } catch (e) {
        if (cancelled) return;
        setError('Failed to load character library.');
      } finally {
        if (!cancelled) {
          setLoadingCharacters(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, user, navigate]);

  useEffect(() => {
    const knownValues = new Set(
      Object.values(FILTER_GROUPS).flat().map((value) => value.toLowerCase()),
    );
    const nextOtherKeywords = new Set();
    libraryCharacters.forEach((character) => {
      (character.keywords || []).forEach((keyword) => {
        const normalized = keyword.toLowerCase();
        if (!knownValues.has(normalized)) {
          nextOtherKeywords.add(normalized);
        }
      });
    });
    setOtherKeywords(Array.from(nextOtherKeywords).sort());
  }, [libraryCharacters]);

  const selectedCharacter = useMemo(
    () => libraryCharacters.find((character) => character.id === selectedCharacterId) || null,
    [libraryCharacters, selectedCharacterId],
  );

  const filteredCharacters = useMemo(() => {
    let list = libraryCharacters;

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      list = list.filter((character) => {
        if (character.name?.toLowerCase().includes(query)) return true;
        if (character.personality?.toLowerCase().includes(query)) return true;
        if (character.physicalAppearance?.toLowerCase().includes(query)) return true;
        if (character.keywords?.some((keyword) => keyword.toLowerCase().includes(query))) return true;
        return false;
      });
    }

    const filterEntries = Object.entries(activeFilters).filter(([, values]) => values.length > 0);
    if (filterEntries.length > 0) {
      list = list.filter((character) => (
        filterEntries.every(([, values]) => values.some((value) => hasKeyword(character.keywords, value)))
      ));
    }

    return list;
  }, [libraryCharacters, searchQuery, activeFilters]);

  const hasAnyFilter = Object.values(activeFilters).some((values) => values.length > 0);

  const toggleFilter = (group, value) => {
    setActiveFilters((current) => {
      const currentValues = current[group] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];
      return { ...current, [group]: nextValues };
    });
  };

  const clearFilters = () => {
    setActiveFilters({ Sex: [], Age: [], Race: [], Other: [] });
  };

  const handleContinue = async () => {
    if (!selectedCharacterId) {
      setError('Select a character to continue.');
      return;
    }
    if (!scanResult?.productDescription?.trim()) {
      setError('Product description is missing. Go back and finish the website step.');
      return;
    }

    if (selectedCharacterId !== persistedCharacterId) {
      setSaving(true);
      setError('');
      try {
        const result = await updateMarketingCharacterSelection({
          selectedCharacterId,
        });
        setPersistedCharacterId(result?.selectedCharacterId || null);
      } catch (e) {
        setError('Failed to save character selection.');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    trackMetaCustomEvent('MarketingOnboardingCharacterSelected', {
      character_id: selectedCharacterId,
      character_name: selectedCharacter?.name || undefined,
    });

    const productImageUrls = scanResult?.productImageUrls || [];
    const attachmentUrls = productImageUrls.filter(Boolean);
    (async () => {
      try {
        const ideasResp = await generateUgcIdeas({
          description: scanResult.productDescription.trim(),
          numIdeas: 1,
          characterName: selectedCharacter?.name || null,
          productImageUrls,
        });

        const firstIdea = ideasResp?.ideas?.[0];
        if (!firstIdea) return;

        const script = firstIdea.detailed_script || firstIdea.script || firstIdea.concept || '';
        const fullPrompt = buildUgcPrompt(script, false);
        const projectId = Math.random().toString(36).substring(2, 15);

        await initializeProject(projectId, fullPrompt, '9:16');
        await startAgentCompose(
          projectId,
          fullPrompt,
          [],
          'fast',
          attachmentUrls,
          'marketing_onboarding',
        );
      } catch (kickoffError) {
        console.error('Marketing onboarding UGC kickoff failed:', kickoffError);
      }
    })();

    navigate('/projects', { replace: true });
  };

  return (
    <div className="post-signup-root">
      <div className="post-signup-card post-signup-card-wide post-signup-card-character-wide">
        <div className="post-signup-header">
          <h2 className="post-signup-title">Choose a character</h2>
          <p className="post-signup-subtitle">
            Pick a character from the library to use as the starting persona for your marketing
            content.
          </p>
        </div>

        {loadingCharacters ? (
          <div className="post-signup-waiting">Loading character library…</div>
        ) : (
          <>
            <div className="post-signup-character-layout">
              <aside className="post-signup-character-sidebar">
                <div className="post-signup-character-filter-shell">
                  <input
                    type="text"
                    className="post-signup-character-search"
                    placeholder="Search name, personality, or keywords..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />

                  <div className="post-signup-character-filter-groups">
                    {Object.entries(FILTER_GROUPS).map(([group, values]) => (
                      <div key={group} className="post-signup-character-filter-group">
                        <span className="post-signup-character-filter-label">{group}</span>
                        <div className="post-signup-character-filter-chips">
                          {values.map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={`post-signup-character-filter-chip${activeFilters[group]?.includes(value) ? ' is-active' : ''}`}
                              onClick={() => toggleFilter(group, value)}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {otherKeywords.length > 0 && (
                      <div className="post-signup-character-filter-group">
                        <span className="post-signup-character-filter-label">Other</span>
                        <div className="post-signup-character-filter-chips">
                          {otherKeywords.slice(0, 24).map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={`post-signup-character-filter-chip${activeFilters.Other?.includes(value) ? ' is-active' : ''}`}
                              onClick={() => toggleFilter('Other', value)}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {hasAnyFilter && (
                    <div className="post-signup-character-active-filters">
                      {Object.entries(activeFilters).flatMap(([group, values]) => (
                        values.map((value) => (
                          <button
                            key={`${group}-${value}`}
                            type="button"
                            className="post-signup-character-filter-chip is-active"
                            onClick={() => toggleFilter(group, value)}
                          >
                            {value}
                          </button>
                        ))
                      ))}
                      <button
                        type="button"
                        className="post-signup-character-clear"
                        onClick={clearFilters}
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              </aside>

              <div className="post-signup-character-main">
                {!!libraryCharacters.length && (
                  <div className="post-signup-character-grid">
                    {filteredCharacters.map((character) => {
                      const isSelected = character.id === selectedCharacterId;
                      const description = character.personality || character.physicalAppearance || '';
                      return (
                        <button
                          key={character.id}
                          type="button"
                          className={`post-signup-character-card${isSelected ? ' is-selected' : ''}`}
                          onClick={() => {
                            setSelectedCharacterId(character.id);
                            setError('');
                          }}
                        >
                          <div className="post-signup-character-image-wrap">
                            <div className="post-signup-character-placeholder">
                              {character.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          </div>
                          <div className="post-signup-character-name">
                            {character.name || 'Untitled character'}
                          </div>
                          {description ? (
                            <div className="post-signup-character-description">
                              {description.length > 120 ? `${description.slice(0, 120)}...` : description}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}

                {!libraryCharacters.length && (
                  <div className="post-signup-hint">No characters are available in the library yet.</div>
                )}

                {!!libraryCharacters.length && filteredCharacters.length === 0 && (
                  <div className="post-signup-hint">No characters match your current search or filters.</div>
                )}
              </div>
            </div>

            {selectedCharacter && (
              <div className="post-signup-inline-note">
                Selected character: {selectedCharacter.name || 'Untitled character'}
              </div>
            )}

            {error && <div className="post-signup-error">{error}</div>}

          </>
        )}
      </div>

      {!loadingCharacters && (
        <div className="post-signup-bottom-bar">
          <div className="post-signup-bottom-bar-inner post-signup-bottom-bar-inner-character">
            <div className="post-signup-bottom-meta">
              {selectedCharacter
                ? `Selected character: ${selectedCharacter.name || 'Untitled character'}`
                : 'Choose a character to continue.'}
            </div>
            <div className="post-signup-bottom-actions">
              <button
                type="button"
                className="post-signup-link-btn"
                onClick={() => navigate('/onboarding/marketing')}
                disabled={saving}
              >
                Previous step
              </button>
              <button
                type="button"
                className="post-signup-submit"
                onClick={handleContinue}
                disabled={saving || !selectedCharacterId || scanStatus !== 'completed'}
              >
                {saving ? 'Saving…' : 'Create my first video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingCharacterOnboardingPage;
